import { Component, HostListener, OnInit } from '@angular/core';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { DateFormatter } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { DateUtil } from '@ddt-web/generic-utils';
import { DefaultScrollableHoldingsTableConfig } from '@ddt-web/holdings';
import { UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import { combineLatest, Observable, of } from 'rxjs';

import { ModelsBrowseDataService, ModelsBrowseLinkActionsService } from '../services';
import { ModelsBrowseModelsMappingService } from '../services/models-browse-models-mapping.service';

@UntilDestroy()
@Component({
	selector: 'mb-family-changes-to-holdings',
	templateUrl: './family-changes-to-holdings.component.html',
	styleUrls: ['./family-changes-to-holdings.component.scss']
})
export class FamilyChangesToHoldingsComponent implements OnInit {

	@InjectConfig(ModelsExperienceConfigKey.SHOW_FUND_PERF_TABLE_LINK) private readonly showFundPerfTableLink = true;
	@InjectConfig(ModelsExperienceConfigKey.MULTI_ROW_LEFT_HEADER) private readonly multiRowLeftHeader = false;
	@InjectConfig(ModelsExperienceConfigKey.CHANGES_TO_HOLDINGS_HEADER_COLUMNS) private readonly changesToHoldingsHeaderColumns = [];
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PORTFOLIO_LINKS) private readonly enablePortfolioLinks = true;
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.DISABLE_360_LINKAGE) public readonly disableLinkageTo360: boolean = false;

	public modelIdsList: Array<string>;
	public modelIds$: Observable<Array<string>>;
	public familyIds$: Observable<Array<string>>;
	public familyId: string;
	public groupId: string;
	public config = DefaultScrollableHoldingsTableConfig.CHANGES_TO_HOLDINGS_TABLE;
	public asOfDate: string;

	// flag introduced to hide the changes to holdings table if model family (all models) on-boarded for the first time
	public noPreviousHoldings: boolean;
	public fetchModelsList$: Observable<string[]>;

	constructor(
		private urlService: UrlService,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private modelsBrowseMappingService: ModelsBrowseModelsMappingService,
		private modelsBrowseLinkActionsService: ModelsBrowseLinkActionsService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public ngOnInit(): void {
		this.familyId = this.urlService.getModelFamilyId();
		this.groupId = this.urlService.getModelGroupId();
		if (!this.familyId) {
			// handled on parent level
			return;
		}
		this.familyIds$ = of([this.familyId]);
		// get the models list if the feature flag is enabled
		this.fetchModelsList$ = this.modelsBrowseMappingService.getModelsList(this.familyId, this.groupId);

		combineLatest([
			this.modelsBrowseDataService.fetchFamilyHoldings(this.familyId),
			this.fetchModelsList$
		]).pipe(untilDestroyed(this))
			.subscribe(([holdings, modelsList]) => {
				this.noPreviousHoldings = holdings.every(holding => get(holding, 'noPreviousHoldings', false));
				const asOfDate = get(holdings[0], 'metadata.currentRebalanceDate');
				if (asOfDate) {
					this.asOfDate = new DateFormatter(this.dateFormat).format(asOfDate);
				}
				// filter holdings based on modelsList
				holdings = modelsList ? holdings.filter(model => modelsList.includes(model.id)) : holdings;

				this.modelIdsList = holdings.map(model => model.id);

				const modelIdAndConfig = this.modelsBrowseMappingService.setModelIdsAndConfigForComponentData(this.modelIdsList);
				this.setModelIdAndConfig(modelIdAndConfig);
			});
	}

	public redirectTo360(portfolioId: string): void {
		if (this.disableLinkageTo360) {
			return;
		}
		const event = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + `:changes to holdings table:${this.familyId}:${portfolioId}`;
		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent(event, { toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE })
		);
		this.modelsBrowseLinkActionsService.redirectOnLinkAction(SharedApplicationConstants.PORTFOLIO_ANALYSER_PAGE, ApplicationPathConstants.PA_PATHS.OVERVIEW, portfolioId);
	}

	public updateTableDataForSelectedPortfolio(modelId: string): void {
		this.modelIds$ = of([modelId]);
	}

	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		const modelIdAndConfig = this.modelsBrowseMappingService.setModelIdsAndConfigForComponentData(this.modelIdsList);
		this.setModelIdAndConfig(modelIdAndConfig);
	}

	private setModelIdAndConfig(modelIdAndConfig: { modelIds: Observable<string[]>; config: Object }): void {
		this.modelIds$ = modelIdAndConfig.modelIds;
		this.config.options.rightColClass = modelIdAndConfig.config['rightColClass'];
		this.config.options.leftColClass = modelIdAndConfig.config['leftColClass'];
	}
}
